import React, { useState, useCallback, useEffect, useRef } from "react";
import { RemocalWidget, shouldShowRemocalWidget } from "@jsq/common-components";
import { Flex, Banner, EmptyState, QueryLoader, JSQLogo, Loader } from "@jsq/ds/core";
import { Column } from "@jsq/ds/layouts";
import { networkedActivationClient } from "~/apollo/client";
import { useAppContext } from "~/contexts/appContext";
import { useUserLoginMutation, useGetSsoStatusForAdminQuery } from "~/graphql/mainHooks";
import { setCSRFToken } from "~/utils/csrf";
import { useLocalStorage } from "~/utils/localStorage";
import { useHandleSuccessfulLogin, NoPermissionError, type CurrentUser } from "./hooks";
import { LoginCard } from "./LoginCard";
import { LoginForm } from "./LoginForm";
import { SSOButton } from "./SSOButton";
import * as loginStyles from "./styles_vanilla.css";

type LoginFormValues = {
  email: string;
  password: string;
  verificationCode: string;
  rememberMe: boolean;
};

const USER_ERRORS: Readonly<string[]> = [
  "JSQGraphQLErrorCode.INVALID_CREDENTIALS",
  "JSQGraphQLErrorCode.TWO_FACTOR_AUTHENTICATION_FAILED",
];

const LIMIT_EXCEEDED_ERRORS: Readonly<string[]> = [
  "JSQGraphQLErrorCode.LOGIN_ATTEMPT_LIMIT_EXCEEDED",
  "JSQGraphQLErrorCode.MFA_ATTEMPT_LIMIT_EXCEEDED",
];

const ERROR_MESSAGES = {
  USER_ERROR:
    "Your username, password, or two-factor authentication code is incorrect. Please try again.",
  LIMIT_ERROR:
    "Login attempt limit exceeded. Further login attempts will be blocked for 5 minutes. If you continue to have issues, please contact support.",
  SYSTEM_ERROR: "An error occurred while signing in. Please try again.",
} as const;

const ADMIN_REMEMBER_ME_KEY = "admin_remember_me";

export const Login: React.FC = () => {
  const { user, updateUser } = useAppContext();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [noPerms, setNoPerms] = useState(false);
  const { setLocalStorageValue, removeLocalStorageValue } =
    useLocalStorage<string>(ADMIN_REMEMBER_ME_KEY);
  const [login] = useUserLoginMutation({
    context: { unauthenticated: true },
    client: networkedActivationClient,
  });
  const { redirectOnSuccessfulLogin } = useHandleSuccessfulLogin();

  const ssoConfigResponse = useGetSsoStatusForAdminQuery({
    context: { unauthenticated: true },
    client: networkedActivationClient,
  });

  const ssoConfig = ssoConfigResponse.data?.unauthenticatedSsoconfig;
  const shouldShowSSO = ssoConfig && ssoConfig.status !== "disabled";
  const shouldShowEmailLogin = !ssoConfig || ssoConfig.status !== "required";
  const shouldShowDivider = shouldShowSSO && shouldShowEmailLogin;

  // An authenticated user on this page is only here to be redirected — either
  // they just logged in, or they arrived mid OAuth handoff with a live session.
  // The redirect itself runs in the effect below (post-paint), so render a
  // loader in the meantime instead of briefly flashing the login form. If a
  // redirect failed with an error, fall through to the form so it's visible.
  const isRedirecting = user?.id !== -1 && !errorMsg;

  // A fresh login and the already-authenticated effect below can both fire
  // after auth succeeds: updateUser() re-renders the component, which trips the
  // effect while handleLoginSubmit is also redirecting. The OAuth handoff binds
  // a single-use challenge, so running it twice invalidates the flow and bounces
  // the user back to the workspace picker. Guard so the redirect runs once.
  const redirectInitiatedRef = useRef(false);
  const initiateRedirect = useCallback(
    (currentUser: CurrentUser) => {
      if (redirectInitiatedRef.current) {
        return;
      }
      redirectInitiatedRef.current = true;
      redirectOnSuccessfulLogin(currentUser).catch((err) => {
        if (err instanceof NoPermissionError) {
          setNoPerms(true);
        } else {
          setErrorMsg(ERROR_MESSAGES.SYSTEM_ERROR);
        }
      });
    },
    [redirectOnSuccessfulLogin],
  );

  const handleLoginSubmit = useCallback(
    async (values: LoginFormValues) => {
      setErrorMsg(undefined);
      // Re-open the once-guard on each user-initiated submit so a retry after a
      // failed redirect/handoff isn't a permanent no-op (which would strand the
      // user on the loader). Resetting here — rather than in the async catch —
      // keeps the concurrent post-updateUser effect from double-firing, since
      // initiateRedirect latches the guard again before that effect runs.
      redirectInitiatedRef.current = false;
      try {
        const result = await login({
          variables: {
            loginInput: {
              email: values.email,
              password: values.password,
              userContext: "arena",
              arenaDomain: "admin",
              verificationCode: values.verificationCode,
            },
          },
          context: { unauthenticated: true },
        });

        const loginResponse = result.data?.login;

        if (!loginResponse) {
          setErrorMsg(ERROR_MESSAGES.SYSTEM_ERROR);
          return;
        }

        if (loginResponse.__typename === "LoginObject") {
          if (values.rememberMe) {
            setLocalStorageValue(values.email);
          } else {
            removeLocalStorageValue();
          }
          setCSRFToken(loginResponse.csrfToken);
          updateUser?.(loginResponse.user);
          initiateRedirect(loginResponse.user);
        } else if (loginResponse.__typename === "MutationErrorResponse") {
          if (USER_ERRORS.includes(loginResponse.errorCode)) {
            setErrorMsg(ERROR_MESSAGES.USER_ERROR);
          } else if (LIMIT_EXCEEDED_ERRORS.includes(loginResponse.errorCode)) {
            setErrorMsg(ERROR_MESSAGES.LIMIT_ERROR);
          } else {
            setErrorMsg(ERROR_MESSAGES.SYSTEM_ERROR);
          }
        } else {
          setErrorMsg(ERROR_MESSAGES.SYSTEM_ERROR);
        }
      } catch {
        // Redirect/handoff failures are handled inside initiateRedirect; this
        // only catches the login mutation itself failing (e.g. network error).
        setErrorMsg(ERROR_MESSAGES.SYSTEM_ERROR);
      }
    },
    [login, initiateRedirect, removeLocalStorageValue, setLocalStorageValue, updateUser],
  );

  // user is already logged in (e.g. arriving at the login page mid OAuth
  // handoff with a live session) — redirect them without re-authenticating.
  useEffect(() => {
    if (user?.id !== -1) {
      initiateRedirect({
        canAccessAdminArena: user.canAccessAdminArena ?? false,
        isComplianceUser: user.isComplianceUser ?? false,
        isKycUser: user.isKycUser ?? false,
        isTreasuryUser: user.isTreasuryUser ?? false,
        isCanopyUser: user.isCanopyUser ?? false,
      });
    }
  }, [user, initiateRedirect]);

  return (
    <QueryLoader result={ssoConfigResponse} width="100vw" height="100vh">
      {shouldShowRemocalWidget() && <RemocalWidget />}
      <Flex justifyContent="center" className={loginStyles.loginColumnContainer}>
        {noPerms ? (
          <EmptyState
            illustration="searching"
            title="There's nothing here!"
            description="This page doesn't exist, or you don't have permission to view it. Please contact support for assistance."
          />
        ) : isRedirecting ? (
          <Loader loading width="100%" height="100vh" />
        ) : (
          <Column size="sm">
            <Flex flexDirection="column" alignItems="center">
              <JSQLogo className={loginStyles.logo} />
              <LoginCard cardTitle="Sign in to admin tools">
                {shouldShowSSO && <SSOButton providerName={ssoConfig?.name || "Okta"} />}
                {shouldShowDivider && (
                  <div className={loginStyles.dividerContainer}>
                    <div role="presentation" className={loginStyles.dividerBorder} />
                    <span className={loginStyles.dividerContent}>OR</span>
                    <div role="presentation" className={loginStyles.dividerBorder} />
                  </div>
                )}
                {errorMsg && (
                  <Banner
                    className={loginStyles.loginFormBanner}
                    variant="danger"
                    title="Sign in Failed"
                    description={errorMsg}
                    data-cy="signin-error"
                  />
                )}
                {shouldShowEmailLogin && <LoginForm onSubmit={handleLoginSubmit} />}
              </LoginCard>
            </Flex>
          </Column>
        )}
      </Flex>
    </QueryLoader>
  );
};
